<!--
 2025-05-25: Added green indicator for WhatsApp selection on mdi-plus icon
 2025-05-25: Moved WhatsApp templates selector into left menu
-->

<template>
  <v-sheet v-hotkey="filterKeyMap" class="">
    <v-divider class="mb-3" />
    <div v-if="isSMS" class="d-flex justify-space-between text-caption">
      <div class="warning--text">
        <span v-show="showError">
          SMS message can't contains more than {{ smsCharLimit }} chars
        </span>
      </div>
      <div>{{ charCounter }} / {{ smsCharLimit }}</div>
    </div>
    <div class="d-flex align-center pb-3 pt-0 px-2 pl-1 border-top">
      <!-- Stop button when AI is loading -->
      <v-btn
        v-if="loadingAi"
        icon
        class="channel-icon"
        @click="cancelAiGeneration()"
      >
        <v-icon size="22">mdi-stop</v-icon>
      </v-btn>

      <!-- Regular menu when AI is not loading -->
      <v-menu
        v-if="
          !loadingAi && (!isMobile || !message.length || isWhatsappSelected)
        "
        bottom
        offset-y
      >
        <template #activator="{ on, attrs }">
          <v-btn
            icon
            class="actions-menu-icon position-relative"
            color="grey darken-1"
            v-bind="attrs"
            v-on="on"
          >
            <v-icon size="22">mdi-plus</v-icon>
            <div v-if="isWhatsappSelected" class="whatsapp-indicator" />
          </v-btn>
        </template>
        <v-list class="more-chat-actions" dense>
          <v-list-item v-if="isWhatsappSelected" @click="openWhatsappTemplates">
            <v-list-item-icon class="green--text mr-3">
              <v-icon size="20">mdi-whatsapp</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>{{
                $t('whatsapp_templates')
              }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>

          <v-list-item
            :disabled="(isCommunicationAgent || isSalesAgent) && !iAmAssigned"
            @click="openPreMadeMessages"
          >
            <v-list-item-icon class="purple--text mr-3">
              <v-icon size="20">mdi-message-text-outline</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>{{
                $t('premade_messages')
              }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>

          <v-list-item
            v-if="showModule('ai')"
            @click="loadingAi ? cancelAiGeneration() : triggerAiAnswer()"
          >
            <v-list-item-icon class="cyan--text mr-3">
              <v-icon size="20">
                {{ loadingAi ? 'mdi-close-thick' : 'mdi-robot' }}
              </v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>
                {{
                  loadingAi
                    ? $t('cancel_ai_generation')
                    : $t('generate_with_ai')
                }}
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>

          <v-list-item v-if="showModule('ai')" @click="openMediaUpload">
            <v-list-item-icon class="info--text mr-3">
              <v-icon size="20">mdi-upload</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>{{ $t('Upload media') }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-menu>
      <!-- AI animation when loading -->
      <v-btn v-if="loadingAi" icon class="mx-1 channel-icon">
        <div
          v-if="loadingAi"
          class="mx-1 d-flex align-center justify-center"
          style="width: 38px; height: 38px"
        >
          <ai-anim />
        </div>
      </v-btn>

      <!-- Regular channel menu when not loading -->
      <v-menu v-else bottom offset-y>
        <template #activator="{ on, attrs }">
          <v-btn
            icon
            class="mx-1 channel-icon"
            :color="CHANNELS[messageModule]?.color"
            v-bind="attrs"
            v-on="on"
          >
            <v-icon size="22">{{ CHANNELS[messageModule]?.icon }}</v-icon>
          </v-btn>
        </template>
        <v-list dense>
          <v-list-item
            v-for="item in modules"
            :key="item"
            @click="messageModulePicker = item"
          >
            <v-list-item-icon :class="`${CHANNELS[item]?.color}--text mr-3`">
              <v-icon size="20">
                {{ CHANNELS[item]?.icon }}
              </v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>{{ CHANNELS[item].label }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-menu>
      <div class="flex-grow-1 d-flex align-center">
        <v-textarea
          ref="messageRef"
          v-model="message"
          :placeholder="
            disableWhatsapp
              ? whatsappDisabledPlaceholder
              : 'Type your message...'
          "
          auto-grow
          :rows="1"
          :max-rows="3"
          dense
          :readonly="disableWhatsapp"
          outlined
          color="white"
          hide-details
          background-color="grey lighten-4"
          class="rounded-lg font-weight-medium message-input"
          @keyup="onMsgDown"
        />
        <div class="ml-2">
          <div class="d-flex align-end">
            <v-btn
              icon
              :class="cantSendMessage ? 'grey' : 'cyan darken-2'"
              color="white"
              :loading="sendMessageLoading"
              :disabled="cantSendMessage"
              @click="sendMessage"
            >
              <v-icon size="20">mdi-send</v-icon>
            </v-btn>
          </div>
        </div>
      </div>
    </div>
    <div v-if="tooRisky" class="text-center">
      <span class="warning--text">
        Reservation is too risky, only admins can react
      </span>
    </div>
    <div v-if="writeActions.length > 0">
      <span
        v-for="(action, index) in writeActions"
        :key="`action-${index}`"
        class="text-caption secondary--text"
      >
        {{ action.user }} is typing..
      </span>
    </div>
    <div class="d-flex justify-space-between w-100 align-end">
      <div class="d-flex align-center">
        <div style="display: none">
          <!-- Hidden components for functionality -->
          <pre-made-messages-popup
            ref="preMadeMessagesPopup"
            :disabled="(isCommunicationAgent || isSalesAgent) && !iAmAssigned"
            :after-message-chosen="afterMessageChosen"
          />

          <div style="display: none">
            <gallery
              ref="galleryUpload"
              :images="[]"
              hide-file-count
              :compress="false"
              icon="mdi-upload"
              icon-color="info"
              uploadable
              :thumbnails="true"
              button-text=""
              :cols="12"
              :folder="`/conversations/${conversationId}/uploads`"
              :on-change="onImageAdded"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- WhatsApp Templates Dialog -->
    <v-dialog v-model="whatsappTemplatesDialog" max-width="700" scrollable>
      <v-card class="elevation-12">
        <v-card-title class="black--text pa-4">
          <span class="green--text">
            <v-icon class="mr-3" color="green" size="28">mdi-whatsapp</v-icon>
          </span>
          <span class="text-h6 text-sm-h5 font-weight-medium">{{
            $t('whatsapp_templates')
          }}</span>
          <v-spacer />
          <v-btn icon color="white" @click="whatsappTemplatesDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <!-- Search Bar -->
        <v-card-text class="py-4 grey lighten-5">
          <v-text-field
            v-model="templateSearchQuery"
            :placeholder="$t('Search templates by name or content...')"
            prepend-inner-icon="mdi-magnify"
            outlined
            dense
            hide-details
            clearable
            class="template-search-field"
          />
        </v-card-text>

        <v-card-text class="pa-0 template-list-container">
          <v-list v-if="filteredTemplates.length > 0" class="py-0">
            <div
              v-for="(template, index) in filteredTemplates"
              :key="`template_${template.name}`"
            >
              <v-list-item
                class="px-6 py-2 template-item"
                @click="selectTemplate(template)"
              >
                <v-list-item-content>
                  <v-list-item-title
                    class="text-body-1 font-weight-medium mb-1"
                  >
                    {{ template.name }}
                  </v-list-item-title>
                  <v-list-item-subtitle
                    v-if="template.body"
                    class="text-caption grey--text text--darken-1"
                  >
                    {{ template.body.substring(0, 100)
                    }}{{ template.body.length > 100 ? '...' : '' }}
                  </v-list-item-subtitle>
                  <v-chip
                    v-if="template.lang !== 'en'"
                    x-small
                    color="green"
                    text-color="white"
                    class="mt-2"
                  >
                    {{ template.lang.toUpperCase() }}
                  </v-chip>
                </v-list-item-content>
                <v-list-item-action>
                  <v-icon color="green">mdi-chevron-right</v-icon>
                </v-list-item-action>
              </v-list-item>
              <v-divider v-if="index < filteredTemplates.length - 1" />
            </div>
          </v-list>
          <div v-else class="pa-8 text-center grey--text template-empty-state">
            <v-icon size="48" color="grey lighten-2" class="mb-4">
              mdi-text-search
            </v-icon>
            <div class="text-body-1">{{ $t('No templates found') }}</div>
            <div class="text-caption mt-2">
              {{ $t('Try adjusting your search criteria') }}
            </div>
          </div>
        </v-card-text>

        <v-card-actions class="pa-4 grey lighten-5">
          <v-spacer />
          <v-btn
            text
            color="grey darken-1"
            class="px-6"
            @click="whatsappTemplatesDialog = false"
          >
            {{ $t('cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-sheet>
</template>
<script>
import CommonFunctions from 'components/mixins/common_functions'
import PermissionsMixin from 'components/mixins/permissions-mixin'
import DeviceMixin from 'components/mixins/device-mixin'
import get from 'lodash/fp/get'
import axios from 'axios'
import { mapGetters, mapState, mapActions } from 'vuex'
import PreMadeMessagesPopup from 'components/conversations/pre-made-messages-popup'
import moment from 'moment'
import AiAnim from 'components/loaders/ai-anim.vue'
import Gallery from 'components/common/gallery.vue'

const CHANNELS = {
  whatsapp: {
    label: 'Whatsapp',
    icon: 'mdi-whatsapp',
    color: 'green',
  },
  sms: {
    label: 'SMS',
    icon: 'mdi-message-text',
    color: 'blue',
  },
  email: {
    label: 'Email',
    icon: 'mdi-email',
    color: 'red',
  },
  note: {
    label: 'Note',
    icon: 'mdi-note-text',
    color: 'orange',
  },
  channel: {
    label: 'Channel',
    icon: 'mdi-forum',
    color: 'purple',
  },
}

const whatsappDisabledPlaceholder = 'Please select a template'
export default {
  props: ['newConvUser'],
  mixins: [CommonFunctions, PermissionsMixin, DeviceMixin],
  data() {
    return {
      CHANNELS,
      whatsappDisabledPlaceholder,
      salesStatus: null,
      message: '',
      messageModulePicker: null,
      reportedStart: null,
      loadingAi: false,
      pendingApproval: false,
      charCounter: 0,
      smsCharLimit: 306,
      showError: false,
      is_pre_made: false,
      content_sid: null,
      serviceCallSuggestedPayload: null,
      whatsappTemplatesDialog: false,
      templateSearchQuery: '',
    }
  },
  mounted() {
    if (this.newConvUser) return

    this.refresConversation = setInterval(async () => {
      try {
        let aiGeneration = await axios.get('/api/conversations/ai-generation', {
          params: {
            conversation_id: this.conversation.id,
          },
        })

        this.loadingAi = aiGeneration.data.status === 'in_progress'
        this.pendingApproval = aiGeneration.data.status === 'generated'

        this.$emit('update-conversation-fun-level')
        // await this.$store.dispatch('conversationUpdate', {
        //   escalated: true,
        //   id: this.conversation.id,
        // })
        this.$emit('update-conversations')
        if ($('.scrollable-dvr-partial')[0]) {
          $('.scrollable-dvr-partial')[0].scrollTop = $(
            '.scrollable-dvr-partial'
          )[0].scrollHeight
        }
      } catch (e) {
        clearInterval(this.refresConversation)
      }
    }, 5000)
  },
  computed: {
    ...mapState([
      'user',
      'showTagPop',
      'whatsappTemplates',
      'serviceCallAISuggestion',
      'sendMessageLoading',
    ]),
    ...mapGetters(['currentUserId']),
    ...mapGetters('users', ['allUsersMap']),
    filteredTemplates() {
      if (!this.templateSearchQuery) {
        return this.whatsappTemplates || []
      }
      const query = this.templateSearchQuery.toLowerCase()
      return (this.whatsappTemplates || []).filter(template => {
        const nameMatch =
          template.name && template.name.toLowerCase().includes(query)
        const bodyMatch =
          template.body && template.body.toLowerCase().includes(query)
        return nameMatch || bodyMatch
      })
    },
    isUsersTab() {
      return this.$route.params.paramsTab === 'users'
    },
    filterKeyMap() {
      return {
        'ctrl+enter': !this.cantSendMessage && this.sendMessage,
      }
    },
    iAmAssigned() {
      return (
        this.conversation.assigned_user_id &&
        this.conversation.assigned_user_id === this.currentUserId
      )
    },
    tooRisky() {
      return (
        this.reservation.source === 'Designedvr.com' &&
        this.reservation.status === 'inquiry' &&
        this.daysAhead(this.reservation.check_in) < 5
      )
    },
    isNote() {
      return this.messageModule === 'note'
    },
    isSMS() {
      return this.messageModule === 'sms'
    },
    writeActions() {
      let res = []
      if (
        this.user &&
        this.conversation.extra_data &&
        this.conversation.extra_data.actions
      ) {
        this.conversation.extra_data.actions.forEach(action => {
          if (
            action.user_id !== this.currentUserId &&
            action.type === 'start_writing' &&
            this.lessThenOneMinAgo(new Date(action.at))
          ) {
            res.push(action)
          }
        })
      }
      return res
    },
    pushedMessage() {
      return this.$store.state.pushedMessage
    },
    messageModule: {
      get() {
        if (this.messageModulePicker) {
          return this.messageModulePicker
        } else if (
          this.conversation.extra_data &&
          this.conversation.extra_data.last_message
        ) {
          let module = this.conversation.extra_data.last_message.module
          if (
            module !== 'whatsapp' &&
            module !== 'email' &&
            module !== 'sms' &&
            module !== 'note'
          ) {
            module = 'channel'
          }
          return module
        } else if (this.newConvUser) {
          return this.modules[0]
        }
        return null
      },
      set(value) {
        this.messageModulePicker = value
      },
    },
    conversation() {
      return this.$store.state.chosenChatConversation || {}
    },
    conversationId() {
      return get('id', this.$store.state.chosenChatConversation)
    },
    reservation() {
      return this.$store.getters.chosenChatReservation
    },
    isWhatsappSelected() {
      return this.messageModule === 'whatsapp'
    },
    disableWhatsapp() {
      if (!this.isWhatsappSelected) return false
      if (this.newConvUser) return true
      const lastMsg = [...this.conversation.thread_plus_dvr_log]
        .reverse()
        .find(msg => msg.module === 'whatsapp' && msg.by === 'guest')
      if (!lastMsg) return true
      const diff = moment().diff(moment(lastMsg.sent_at), 'days')
      return diff > 0
    },
    cantSendMessage() {
      return (
        (this.disableWhatsapp && !this.message.length) ||
        !this.messageModule ||
        !this.message ||
        (this.isSMS && this.charCounter > this.smsCharLimit) ||
        (this.tooRisky && !this.isAdmin) ||
        (!this.iAmAssigned && !this.managementRoles) ||
        this.aiDisabled
      )
    },
    modules() {
      const { available_modules } = this.conversation
      if (available_modules) return available_modules
      if (this.newConvUser) {
        const user = this.allUsersMap[this.newConvUser]
        if (!user) return []
        const availableModules = []
        if (user.phone) {
          availableModules.push('whatsapp', 'sms')
        }
        if (user.email) {
          availableModules.push('email')
        }
        return [...availableModules, 'note']
      }
      return []
    },
  },
  watch: {
    conversationId() {
      this.message = ''
      this.is_pre_made = false
      this.content_sid = null
      this.$refs.messageRef.focus()
      if (this.conversation.user_id === +this.$route.query.newConvUser) {
        this.$router.push({
          query: { conversation: this.conversation.guesty_id },
        })
      }
    },
    pushedMessage(val) {
      if (val) {
        this.message = val
        this.is_pre_made = false
        this.content_sid = null
      }
    },
    message(val) {
      if (
        val &&
        (!this.reportedStart ||
          this.$moment
            .duration(this.$moment().diff(this.reportedStart))
            .asSeconds() > 20)
      ) {
        this.reportedStart = this.$moment()
        if (!this.newConvUser) this.markAsStartWriting()
      }
    },
  },
  beforeDestroy() {
    if (this.refresConversation) {
      clearInterval(this.refresConversation)
    }
  },
  methods: {
    ...mapActions(['setServiceCallAISuggestion']),
    onMsgDown(event) {
      if (event.target.value.length > this.smsCharLimit) {
        this.showError = true
      } else {
        this.showError = false
        if (event.ctrlKey && (event.code === 'Enter' || event.keyCode === 13)) {
          this.sendMessage()
          return
        }
      }
      this.charCounter = this.message.length
    },
    removeTag() {
      this.salesStatus = null
    },
    afterMessageChosen(message) {
      this.message = message.body
      this.is_pre_made = true
      this.salesStatus = message.tags[0]
    },
    lessThenOneMinAgo(date) {
      var duration = this.$moment.duration(
        this.$moment().diff(this.$moment(date))
      )
      return duration.asMinutes() < 1
    },
    markAsStartWriting() {
      this.$store.dispatch('conversationUpdateBG', {
        id: this.conversation.id,
        start_writing: true,
      })
    },
    sendMessage() {
      this.$store.dispatch('sendChatMessage', {
        id: this.newConvUser ? 'new' : this.conversation.id,
        message: this.message,
        module: this.messageModule,
        sales_status: this.salesStatus,
        is_pre_made: this.is_pre_made,
        content_sid: this.content_sid,
        reservation_id: this.newConvUser
          ? null
          : this.conversation?.active_reservation?.reservation_guesty_id,
        user_id:
          this.newConvUser ||
          (this.isUsersTab ? this.conversation.user_id : null),
      })
      this.message = ''
      this.is_pre_made = false
      this.content_sid = null
      this.salesStatus = null
      this.charCounter = 0
    },
    selectTemplate(template) {
      this.message = template.body
      this.is_pre_made = true
      this.content_sid = template.content_sid
      this.whatsappTemplatesDialog = false
    },
    openWhatsappTemplates() {
      this.whatsappTemplatesDialog = true
      this.templateSearchQuery = ''
    },
    async triggerAiAnswer() {
      this.loadingAi = true
      this.pendingApproval = true
      this.setServiceCallAISuggestion(false)
      this.serviceCallSuggestedPayload = null
      await axios.post('/api/conversations/ai-generation', {
        conversation_id: this.conversation.id,
      })
    },
    async cancelAiGeneration() {
      await axios.delete('/api/conversations/ai-generation', {
        params: {
          conversation_id: this.conversation.id,
        },
      })
      this.loadingAi = false
      this.pendingApproval = false
    },
    updateMessage(message) {
      this.message = message
    },
    onImageAdded(images) {
      console.log(images)
      this.message += ` ${images[0]}`
    },
    openPreMadeMessages() {
      // Open the pre-made messages dialog programmatically
      this.$refs.preMadeMessagesPopup.dialog = true
    },
    openMediaUpload() {
      // Trigger the file input dialog programmatically
      const fileInput =
        this.$refs.galleryUpload.$el.querySelector('input[type="file"]')
      if (fileInput) {
        fileInput.click()
      }
    },
  },
  components: {
    AiAnim,
    PreMadeMessagesPopup,
    Gallery,
  },
}
</script>
<style lang="scss" scoped>
.chat-box {
  &-content {
    border: 1px solid var(--v-info-base);
  }
}

@keyframes zoomAnimation {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.15);
  }
}

.highlighted-button {
  animation: zoomAnimation 3s ease-in-out 3;
}
</style>
<style scoped>
.border-top {
  border-top: 1px solid var(--v-divider-base);
}
.channel-icon {
  border: 2px solid var(--v-secondary-lighten1) !important;
  background-color: var(--v-secondary-lighten3) !important;
  width: 38px !important;
  height: 38px !important;
}

.actions-menu-icon {
  border: 2px solid var(--v-grey-lighten1) !important;
  background-color: var(--v-grey-lighten3) !important;
  width: 38px !important;
  height: 38px !important;
}

.whatsapp-indicator {
  position: absolute;
  top: -5px;
  right: 2px;
  width: 10px;
  height: 10px;
  background-color: #25d366;
  border-radius: 50%;
  border: 1px solid white;
}

.message-input ::v-deep .v-input__control {
  min-height: auto;
}

.message-input ::v-deep .v-text-field__details {
  display: none;
}

.message-input ::v-deep textarea {
  line-height: 1.4 !important;
  max-height: calc(1.4em * 6 + 16px) !important;
  overflow-y: auto !important;
  padding-top: 3px !important;
  padding-bottom: 3px !important;
  padding-left: 4px !important;
  padding-right: 4px !important;
  caret-color: #333 !important;
  color: #333 !important;
}

.message-input ::v-deep .v-input__slot:before {
  border: none !important;
}

.message-input ::v-deep fieldset {
  border: none !important;
}

.template-item {
  transition: background-color 0.2s ease;
  cursor: pointer;
}

.template-item:hover {
  background-color: rgba(76, 175, 80, 0.08) !important;
}

.template-search-field ::v-deep .v-input__control {
  background-color: white !important;
}

.template-list-container {
  height: 400px;
  overflow-y: auto;
}

.template-empty-state {
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

@media (max-width: 600px) {
  .message-input ::v-deep textarea {
    max-height: calc(1.4em * 3 + 16px) !important;
  }

  .template-list-container {
    height: 300px;
  }
}
</style>
